import React, { useEffect, useState } from 'react';
import LayoutUsuario from '@/components/navegacao/LayoutUsuario';
import PageHeader from '@/components/layout/PageHeader';
import VBox from '@/components/layout/VBox';
import HBox from '@/components/layout/HBox';
import Botao from '@/components/Botao';
import Icon from '@mdi/react';
import { mdiLoading } from '@mdi/js';
import { useAppliance } from '@/lib/useAppliance';
import { useMutation, useQuery, useQueryClient } from 'react-query';
import axios from 'axios';
import { toastError, toastSuccess } from '@/lib/toast';
import { SquidConfigDTO } from '@webip/shared/src/dto/appliance';
import FormSquid from './FormSquid';

const defaultSquid = (): Partial<SquidConfigDTO> => ({
    tipo: 'container_mk',
    ip: '',
    porta: 3128,
    ssh_usuario: '',
    ssh_senha: '',
    ativo: true,
});

export default function SquidPage() {
    const { applianceId, selectedAppliance } = useAppliance();
    const qc = useQueryClient();
    const [model, setModel] = useState<Partial<SquidConfigDTO>>(defaultSquid());
    const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});

    const query = useQuery(
        ['squid-config', applianceId],
        async () => {
            const res = await axios.get<{ ok: boolean; result: SquidConfigDTO }>(
                `/api/appliance/${applianceId}/squid`,
                { withCredentials: true },
            );
            return res.data.result;
        },
        {
            enabled: !!applianceId,
            retry: false,
            onSuccess: (data) => setModel({ ...data, ssh_senha: '' }),
            onError: () => setModel(defaultSquid()),
        },
    );

    useEffect(() => {
        setFieldErrors({});
    }, [applianceId]);

    const mutSave = useMutation({
        mutationFn: async (payload: Partial<SquidConfigDTO>) => {
            if (query.data?.id) {
                return await axios.put(`/api/appliance/${applianceId}/squid`, payload, { withCredentials: true });
            } else {
                return await axios.post(`/api/appliance/${applianceId}/squid`, payload, { withCredentials: true });
            }
        },
        onSuccess: () => {
            toastSuccess('Configuração do Squid salva!');
            qc.invalidateQueries(['squid-config', applianceId]);
        },
        onError: (error: unknown) => {
            const msg = (error as any)?.response?.data?.message ?? 'Erro ao salvar';
            toastError(msg);
        },
    });

    const mutDelete = useMutation({
        mutationFn: async () => {
            return await axios.delete(`/api/appliance/${applianceId}/squid`, { withCredentials: true });
        },
        onSuccess: () => {
            toastSuccess('Configuração do Squid removida!');
            setModel(defaultSquid());
            qc.invalidateQueries(['squid-config', applianceId]);
        },
        onError: (error: unknown) => {
            const msg = (error as any)?.response?.data?.message ?? 'Erro ao remover';
            toastError(msg);
        },
    });

    const handleSave = () => {
        const errors: Record<string, string> = {};
        if (!model.ip) errors.ip = 'IP é obrigatório';
        if (!model.tipo) errors.tipo = 'Tipo é obrigatório';
        if (Object.keys(errors).length > 0) {
            setFieldErrors(errors);
            return;
        }
        mutSave.mutate(model);
    };

    return (
        <LayoutUsuario>
            <VBox style={{ gap: '16px', padding: '16px' }}>
                <PageHeader
                    title={`Squid (Proxy)${selectedAppliance ? ` — ${selectedAppliance.nome}` : ''}`}
                    description="Configure o proxy transparente Squid para o appliance selecionado."
                    actions={applianceId ? (
                        <>
                            {query.data?.id && (
                                <Botao
                                    variant="outline-danger"
                                    disabled={mutDelete.isLoading}
                                    onClick={() => mutDelete.mutate()}
                                >
                                    Remover configuração
                                </Botao>
                            )}
                            <Botao
                                variant="primary"
                                onClick={handleSave}
                                disabled={mutSave.isLoading}
                            >
                                {mutSave.isLoading ? 'Salvando...' : 'Salvar'}
                            </Botao>
                        </>
                    ) : undefined}
                />

                {!applianceId && (
                    <p style={{ color: 'var(--color-muted)', fontStyle: 'italic' }}>
                        Selecione um appliance no menu lateral.
                    </p>
                )}

                {applianceId && query.isLoading && (
                    <HBox style={{ justifyContent: 'center', padding: '32px' }}>
                        <Icon path={mdiLoading} size={1.5} spin />
                    </HBox>
                )}

                {applianceId && !query.isLoading && (
                    <FormSquid
                        value={model}
                        onChange={setModel}
                        fieldErrors={fieldErrors}
                    />
                )}
            </VBox>
        </LayoutUsuario>
    );
}
